import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from '@/components/ui/sidebar.tsx';
import { Building2, LogOut, EllipsisVertical } from 'lucide-react';
import { CampdeskLogo } from '@/components/svg/CampdeskLogo.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu.tsx';
import { auth } from '@/lib/firebase.ts';
import { cn } from '@/lib/utils.ts';
import { Link } from '@tanstack/react-router';
import useSignOut from '@/features/auth/login/hooks/useSignOut.ts';
import useGetUserByEmail from '@/features/dashboard/users/hooks/useGetUserByEmail.ts';
import { APP_MENU_SECTIONS } from '@/features/dashboard/_root/appMenu.ts';
import useActiveAppMenuItem from '@/features/dashboard/_root/hooks/useActiveAppMenuItem.ts';
import GlobalSearch from '@/features/dashboard/globalSearch/components/GlobalSearch.tsx';
import Chip from '@/components/ui/Chip.tsx';
import { EMPTY_VALUE } from '@/components/ui/EmptyValue.tsx';
import InitialsAvatar from '@/components/ui/InitialsAvatar.tsx';
import useMe from '@/features/dashboard/_root/hooks/useMe.ts';
import { companyInitials } from '@/features/auth/workspaces/utils/companyInitials.ts';
import {
  readStoredCompanyId,
  selectCurrentAccess,
} from '@/features/dashboard/_root/access/currentCompany.ts';

const AppSidebar = () => {
  const active = useActiveAppMenuItem();
  const { signOut } = useSignOut();
  const { data: me } = useMe();
  const current = selectCurrentAccess(me, readStoredCompanyId());
  const invitationCount = me?.invitations.length ?? 0;

  return (
    <Sidebar className="border-r-1 border-neutral-100">
      <SidebarHeader className="gap-3 border-b-0">
        <Link to="/app" className="flex items-center justify-center">
          <CampdeskLogo width={'130'} height={'30'} />
        </Link>
        <GlobalSearch />
      </SidebarHeader>

      <SidebarContent>
        {APP_MENU_SECTIONS.map((section) => (
          <SidebarGroup key={section.label}>
            <SidebarGroupLabel>{section.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.map((item) => {
                  const isActive = item === active;

                  return (
                    <SidebarMenuItem key={item.label}>
                      <SidebarMenuButton asChild isActive={isActive}>
                        <Link
                          to={item.to}
                          className="group flex w-full items-center gap-3 no-underline"
                        >
                          <item.icon
                            className={cn(
                              'transition-colors',
                              isActive
                                ? 'text-accent-600'
                                : 'text-neutral-400 group-hover:text-neutral-600',
                            )}
                            size={20}
                          />
                          <span
                            className={cn(
                              isActive && 'font-medium text-base-text',
                            )}
                          >
                            {item.label}
                          </span>
                          {isActive && (
                            <div className="absolute right-0 h-7 w-1 bg-accent-600" />
                          )}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className="mt-auto">
        <DropdownMenu>
          <DropdownMenuTrigger>
            <UserInfo />
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" side="top">
            <DropdownMenuLabel>
              <div className="flex items-center gap-2">
                {current && (
                  <InitialsAvatar
                    tone="plain"
                    size="sm"
                    initials={companyInitials(current.companyName)}
                  />
                )}
                <span className="truncate">
                  {current?.companyName ?? EMPTY_VALUE}
                </span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              asChild
              className="group text-neutral-600 hover:text-base-text"
            >
              <Link to="/auth/workspaces">
                <Building2
                  className="mr-2 text-neutral-400 group-hover:text-neutral-600"
                  size={20}
                />
                <span>Firmy i zaproszenia</span>
                {invitationCount > 0 && (
                  <Chip
                    label={String(invitationCount)}
                    tone="accent"
                    fill="soft"
                    className="ml-auto"
                  />
                )}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="group text-neutral-600 hover:text-base-text"
              onClick={signOut}
            >
              <LogOut
                className="mr-2 text-neutral-400 group-hover:text-neutral-600"
                size={20}
              />
              <span>Wyloguj się</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  );
};

const UserInfo = () => {
  const userEmail = auth.currentUser?.email;
  const { data } = useGetUserByEmail(userEmail ?? undefined);

  const displayName = `${data?.firstName} ${data?.lastName}`;

  return (
    <div className="flex w-full items-center justify-between gap-3 px-1.5 py-2 hover:bg-gray-100">
      <div className="flex w-32 flex-col gap-0.5 text-left">
        <p className="text-sm font-medium text-base-text">{displayName}</p>
        <p className="max-w-[160px] truncate text-xs text-neutral-600">
          {userEmail}
        </p>
      </div>
      <EllipsisVertical size={16} className="text-neutral-600" />
    </div>
  );
};

export default AppSidebar;
